import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import * as THREE from "three";
import {
  BEAM_TARGETS,
  COUNTRY_GEOJSON_URL,
  readLoadingThemeColors,
} from "./loading-globe-config";
import {
  type ActiveShot,
  addDitherAndGrid,
  disposeObject,
  disposeShots,
  loadCountries,
  makeGlobeBody,
  spawnShot,
  updateShots,
} from "./loading-globe-scene";

export type LoadingGlobePhase = "idle" | "auth" | "ready";

interface LoadingGlobeProps {
  phase?: LoadingGlobePhase;
  className?: string;
  style?: CSSProperties;
  density?: number;
}

export function LoadingGlobe({
  phase = "idle",
  className,
  style,
  density = 1,
}: LoadingGlobeProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const fallbackCanvasRef = useRef<HTMLCanvasElement>(null);
  const phaseRef = useRef<LoadingGlobePhase>(phase);
  const [webglFailed, setWebglFailed] = useState(false);
  const hostClassName = ["loading-globe", className].filter(Boolean).join(" ");

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const mount = host;

    const colors = readLoadingThemeColors();
    const primaryColor = new THREE.Color(colors.primary);
    const bgColor = new THREE.Color(colors.bg);
    const starGold = new THREE.Color("#d3ad52").lerp(primaryColor, 0.34);
    if (!canCreateWebGLContext()) {
      setWebglFailed(true);
      return;
    }
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: false,
        alpha: true,
        powerPreference: "high-performance",
      });
      setWebglFailed(false);
    } catch (error) {
      console.warn(
        "[LoadingGlobe] WebGL unavailable, using CSS fallback",
        error,
      );
      setWebglFailed(true);
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.inset = "0";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.imageRendering = "pixelated";
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 80);
    camera.position.set(0, 0, 4.3);

    const globe = new THREE.Group();
    const countryFillLayer = new THREE.Group();
    const countryLayer = new THREE.Group();
    const shotLayer = new THREE.Group();
    const activeShots: ActiveShot[] = [];
    scene.add(globe);
    globe.add(
      makeGlobeBody(bgColor),
      countryFillLayer,
      countryLayer,
      shotLayer,
    );
    addDitherAndGrid({ globe, scene, primaryColor, starGold, density });

    const successRing = new THREE.Mesh(
      new THREE.TorusGeometry(1.18, 0.006, 8, 160),
      new THREE.MeshBasicMaterial({
        color: primaryColor,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
      }),
    );
    scene.add(successRing);

    const state = {
      speed: 1,
      start: performance.now(),
      previous: performance.now(),
      nextShot: 0,
      authPulse: phaseRef.current === "auth" ? 1 : 0,
      authUntil: phaseRef.current === "auth" ? 1.35 : 0,
      lastPhase: phaseRef.current,
    };

    let frame = 0;
    let disposed = false;
    let resizeObserver: ResizeObserver | null = null;

    loadCountries({
      countryLayer,
      countryFillLayer,
      primaryColor,
      isDisposed: () => disposed,
    });

    function resize() {
      const width = Math.max(1, mount.clientWidth);
      const height = Math.max(1, mount.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }

    function animate(now: number) {
      const elapsed = (now - state.start) / 1000;
      const delta = Math.min(0.05, (now - state.previous) / 1000);
      state.previous = now;
      syncPhase(state, phaseRef.current, elapsed);

      if (elapsed > state.nextShot && phaseRef.current !== "ready") {
        spawnShot(shotLayer, activeShots, primaryColor);
        state.nextShot = elapsed + 2.1 + Math.random() * 2.1;
      }

      updateShots(activeShots, delta, shotLayer);
      const starPulse = getStarPulse(elapsed);
      updateNamedObject(scene, "starfield", (object) => {
        const material = (object as THREE.Points)
          .material as THREE.PointsMaterial;
        material.opacity = 0.065 + starPulse * 0.105;
        material.size = 0.011 + starPulse * 0.004;
        object.rotation.z += 0.00008 * delta * 60;
      });

      const targetSpeed = targetSpeedForPhase(
        phaseRef.current,
        elapsed,
        state.authUntil,
      );
      state.speed += (targetSpeed - state.speed) * 0.055;
      state.authPulse = Math.max(0, state.authPulse - 0.018 * delta * 60);
      globe.rotation.y += 0.0028 * state.speed * delta * 60;
      globe.rotation.x = Math.sin(elapsed * 0.35) * 0.045;
      successRing.material.opacity = state.authPulse * 0.42;
      successRing.scale.setScalar(1 + (1 - state.authPulse) * 0.16);
      camera.position.z +=
        ((phaseRef.current === "ready" ? 2.05 : 4.3) - camera.position.z) *
        0.028;

      renderer.render(scene, camera);
      frame = window.requestAnimationFrame(animate);
    }

    resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);
    resize();
    frame = window.requestAnimationFrame(animate);

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      disposeShots(activeShots, shotLayer);
      disposeObject(scene);
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [density]);

  useEffect(() => {
    if (!webglFailed) return;
    const canvas = fallbackCanvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;
    const fallbackCanvas = canvas;
    const mount = host;

    let disposed = false;
    let frame = 0;
    let countries: CountryFeatureCollection | null = null;
    const colors = readLoadingThemeColors();
    const context = fallbackCanvas.getContext("2d");
    if (!context) return;
    const ctx = context;

    fetch(COUNTRY_GEOJSON_URL)
      .then((response) => (response.ok ? response.json() : null))
      .then((geojson) => {
        if (!disposed && geojson)
          countries = geojson as CountryFeatureCollection;
      })
      .catch(() => {
        countries = null;
      });

    function resize() {
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = Math.max(1, mount.clientWidth);
      const height = Math.max(1, mount.clientHeight);
      fallbackCanvas.width = Math.round(width * ratio);
      fallbackCanvas.height = Math.round(height * ratio);
      fallbackCanvas.style.width = `${width}px`;
      fallbackCanvas.style.height = `${height}px`;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    function animate(now: number) {
      drawCanvasFallback({
        context: ctx,
        countries,
        colors,
        density,
        height: mount.clientHeight,
        now,
        phase: phaseRef.current,
        width: mount.clientWidth,
      });
      frame = window.requestAnimationFrame(animate);
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);
    resize();
    frame = window.requestAnimationFrame(animate);

    return () => {
      disposed = true;
      resizeObserver.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [density, webglFailed]);

  return (
    <div
      ref={hostRef}
      className={hostClassName}
      aria-hidden="true"
      style={{ position: "relative", overflow: "hidden", ...style }}
    >
      {webglFailed ? (
        <canvas
          ref={fallbackCanvasRef}
          className="loading-globe__canvas-fallback"
          style={fallbackCanvasStyle}
        />
      ) : null}
    </div>
  );
}

function canCreateWebGLContext() {
  try {
    const canvas = document.createElement("canvas");
    const context =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");
    return Boolean(context);
  } catch {
    return false;
  }
}

const fallbackCanvasStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  imageRendering: "pixelated",
};

interface CanvasFallbackOptions {
  colors: { bg: string; primary: string };
  context: CanvasRenderingContext2D;
  countries: CountryFeatureCollection | null;
  density: number;
  height: number;
  now: number;
  phase: LoadingGlobePhase;
  width: number;
}

interface CountryFeatureCollection {
  features?: Array<{
    geometry?: {
      coordinates?: unknown;
      type?: string;
    };
  }>;
}

type ProjectedPoint = { visible: boolean; x: number; y: number };

function drawCanvasFallback({
  colors,
  context,
  countries,
  density,
  height,
  now,
  phase,
  width,
}: CanvasFallbackOptions) {
  const elapsed = now / 1000;
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.max(1, Math.min(width, height) * 0.36);
  const rotation = elapsed * (phase === "auth" ? 0.42 : 0.18) - 1.34;
  context.clearRect(0, 0, width, height);

  const glow = context.createRadialGradient(
    cx,
    cy,
    radius * 0.05,
    cx,
    cy,
    radius,
  );
  glow.addColorStop(0, withAlpha(colors.primary, 0.24));
  glow.addColorStop(0.42, withAlpha(colors.primary, 0.1));
  glow.addColorStop(1, withAlpha(colors.primary, 0));
  context.fillStyle = glow;
  context.fillRect(0, 0, width, height);

  context.save();
  context.beginPath();
  context.arc(cx, cy, radius, 0, Math.PI * 2);
  context.clip();

  drawDither(context, cx, cy, radius, colors.primary, density, rotation);
  drawGrid(context, cx, cy, radius, rotation, colors.primary);
  if (countries)
    drawCountries(context, countries, cx, cy, radius, rotation, colors.primary);

  context.restore();
  drawBeams(context, cx, cy, radius, rotation, colors.primary, elapsed);
  context.beginPath();
  context.arc(cx, cy, radius, 0, Math.PI * 2);
  context.strokeStyle = withAlpha(colors.primary, 0.32);
  context.lineWidth = 1;
  context.stroke();
}

function drawDither(
  context: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  color: string,
  density: number,
  rotation: number,
) {
  const total = Math.floor(720 * density);
  context.fillStyle = withAlpha(color, 0.34);
  for (let index = 0; index < total; index += 1) {
    const t = index / total;
    const lon = (index * 137.5 + rotation * 42) % 360;
    const lat = Math.asin(1 - 2 * t) * (180 / Math.PI);
    const point = project(lon - 180, lat, rotation, cx, cy, radius);
    if (!point.visible) continue;
    context.globalAlpha =
      0.18 + 0.32 * Math.max(0, 1 - point.x / (cx + radius));
    context.fillRect(point.x, point.y, 1.2, 1.2);
  }
  context.globalAlpha = 1;
}

function drawGrid(
  context: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  rotation: number,
  color: string,
) {
  context.strokeStyle = withAlpha(color, 0.22);
  context.lineWidth = 0.8;
  for (let lat = -60; lat <= 60; lat += 20) {
    drawProjectedLine(
      context,
      range(-180, 180, 4).map((lon) => [lon, lat]),
      cx,
      cy,
      radius,
      rotation,
    );
  }
  for (let lon = -180; lon < 180; lon += 30) {
    drawProjectedLine(
      context,
      range(-80, 80, 4).map((lat) => [lon, lat]),
      cx,
      cy,
      radius,
      rotation,
    );
  }
}

function drawCountries(
  context: CanvasRenderingContext2D,
  countries: CountryFeatureCollection,
  cx: number,
  cy: number,
  radius: number,
  rotation: number,
  color: string,
) {
  context.strokeStyle = withAlpha(color, 0.78);
  context.fillStyle = withAlpha(color, 0.12);
  context.lineWidth = 0.8;
  for (const feature of countries.features ?? []) {
    const geometry = feature.geometry;
    if (!geometry?.coordinates) continue;
    const polygons =
      geometry.type === "Polygon"
        ? [geometry.coordinates]
        : geometry.type === "MultiPolygon"
          ? geometry.coordinates
          : [];
    for (const polygon of polygons as number[][][][]) {
      for (const ring of polygon) {
        drawProjectedLine(context, ring, cx, cy, radius, rotation, true);
      }
    }
  }
}

function drawBeams(
  context: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  rotation: number,
  color: string,
  elapsed: number,
) {
  const start = BEAM_TARGETS[Math.floor(elapsed / 3) % BEAM_TARGETS.length];
  const end = BEAM_TARGETS[(Math.floor(elapsed / 3) + 4) % BEAM_TARGETS.length];
  const a = project(start.lon, start.lat, rotation, cx, cy, radius);
  const b = project(end.lon, end.lat, rotation, cx, cy, radius);
  if (!a.visible || !b.visible) return;
  context.strokeStyle = withAlpha(color, 0.44);
  context.lineWidth = 1.4;
  context.beginPath();
  context.moveTo(a.x, a.y);
  context.quadraticCurveTo(cx, cy - radius * 0.26, b.x, b.y);
  context.stroke();
}

function drawProjectedLine(
  context: CanvasRenderingContext2D,
  coords: number[][],
  cx: number,
  cy: number,
  radius: number,
  rotation: number,
  fill = false,
) {
  let drawing = false;
  context.beginPath();
  for (const coord of coords) {
    const point = project(coord[0], coord[1], rotation, cx, cy, radius);
    if (!point.visible) {
      drawing = false;
      continue;
    }
    if (!drawing) {
      context.moveTo(point.x, point.y);
      drawing = true;
    } else {
      context.lineTo(point.x, point.y);
    }
  }
  if (fill) context.fill();
  context.stroke();
}

function project(
  lon: number,
  lat: number,
  rotation: number,
  cx: number,
  cy: number,
  radius: number,
): ProjectedPoint {
  const lambda = (lon * Math.PI) / 180 + rotation;
  const phi = (lat * Math.PI) / 180;
  const x = Math.cos(phi) * Math.sin(lambda);
  const y = Math.sin(phi);
  const z = Math.cos(phi) * Math.cos(lambda);
  return {
    visible: z > -0.12,
    x: cx + x * radius,
    y: cy - y * radius,
  };
}

function range(from: number, to: number, step: number): number[] {
  const values: number[] = [];
  for (let value = from; value <= to; value += step) values.push(value);
  return values;
}

function withAlpha(color: string, alpha: number): string {
  if (color.startsWith("#") && (color.length === 7 || color.length === 4)) {
    const full =
      color.length === 4
        ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
        : color;
    const r = Number.parseInt(full.slice(1, 3), 16);
    const g = Number.parseInt(full.slice(3, 5), 16);
    const b = Number.parseInt(full.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
}

function syncPhase(
  state: { lastPhase: LoadingGlobePhase; authPulse: number; authUntil: number },
  phase: LoadingGlobePhase,
  elapsed: number,
) {
  if (phase === state.lastPhase) return;
  state.lastPhase = phase;
  if (phase !== "auth") return;
  state.authPulse = 1;
  state.authUntil = elapsed + 1.35;
}

function targetSpeedForPhase(
  phase: LoadingGlobePhase,
  elapsed: number,
  authUntil: number,
) {
  if (phase === "ready") return 1.6;
  if (phase === "auth" && elapsed < authUntil) return 2;
  return 1;
}

function getStarPulse(elapsed: number) {
  const phase = (elapsed % 7.5) / 7.5;
  return phase < 0.2 ? Math.sin((phase / 0.2) * Math.PI) : 0;
}

function updateNamedObject(
  scene: THREE.Scene,
  name: string,
  update: (object: THREE.Object3D) => void,
) {
  const object = scene.getObjectByName(name);
  if (object) update(object);
}
